import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle2, Award, HeartHandshake, Wrench, Gauge, ArrowRight } from "lucide-react";
import useSEO from "../hooks/useSEO";

const values = [
  { icon: Award, title: "Kwaliteit", text: "Elke motor en herstelling voldoet aan de hoogste standaarden." },
  { icon: HeartHandshake, title: "Eerlijk advies", text: "Transparant en oprecht, we denken met je mee." },
  { icon: Wrench, title: "Vakmanschap", text: "Jarenlange technische ervaring met alle merken." },
  { icon: Gauge, title: "Passie voor motoren", text: "Motorrijden zit in ons DNA — dat voel je in alles wat we doen." },
  { icon: CheckCircle2, title: "Uitstekende service", text: "Persoonlijke opvolging van A tot Z, ook na de verkoop." },
];

function PageHeader({ tag, title, sub }) {
  return (
    <section className="relative pt-36 pb-16 bg-[#0A0A0B] border-b border-[#2A2B31] overflow-hidden">
      <div className="absolute top-0 right-0 w-1/3 h-full racing-stripe opacity-[0.04]" />
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 relative">
        <span className="font-mono-tt uppercase text-xs tracking-[0.3em] text-[#E10600]">{tag}</span>
        <h1 className="heading-xl text-5xl md:text-7xl text-white mt-3">{title}</h1>
        {sub && <p className="text-[#9A9CA3] text-lg mt-5 max-w-2xl">{sub}</p>}
      </div>
    </section>
  );
}

export default function Over() {
  useSEO({
    title: "Over TT Motorsports Torhout — motor specialist voor alle merken",
    description: "Ontdek TT Motorsports: jouw motor specialist in Torhout (West-Vlaanderen). Kwaliteit, eerlijk advies, vakmanschap en uitstekende service voor motoronderhoud, herstellingen en verkoop van alle types motoren.",
    keywords: "TT Motorsports, motor specialist Torhout, motor garage, motoronderhoud, motor herstelling, motorzaak West-Vlaanderen",
  });

  return (
    <div data-testid="over-page">
      <PageHeader tag="Over ons" title="Gedreven door passie" sub="TT Motorsports is meer dan een werkplaats. Het is de plek waar techniek, kwaliteit en liefde voor de motor samenkomen." />

      <section className="section-pad bg-[#0A0A0B]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-14 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="heading-xl text-3xl md:text-5xl text-white mb-6">Onze <span className="text-[#E10600]">visie</span></h2>
            <div className="space-y-4 text-[#B8B9BE] leading-relaxed">
              <p>Bij TT Motorsports draait alles om de motor én de rijder. Wat begon vanuit een oprechte passie voor motorsport is uitgegroeid tot een gespecialiseerde zaak waar je terecht kan voor verkoop, onderhoud, herstellingen en onderdelen van alle merken.</p>
              <p>We geloven dat vakmanschap en eerlijkheid het verschil maken. Daarom nemen we de tijd om je motor grondig te bekijken, geven we transparant advies en zorgen we ervoor dat je met een gerust hart de baan opgaat.</p>
              <p>Of je nu een fanatieke crosser bent, een dagelijkse rijder of gewoon op zoek naar je eerste motor — bij ons ben je aan het juiste adres.</p>
            </div>
            <Link to="/contact" className="inline-flex items-center gap-2 mt-8 bg-[#E10600] hover:bg-[#B80500] text-white font-display uppercase font-700 tracking-wide px-6 py-3.5 transition-colors">
              Kom langs <ArrowRight size={18} />
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="relative">
            <div className="aspect-[3/4] overflow-hidden border border-[#2A2B31] bg-[#0A0A0B]">
              <img src="/over-werkplaats.jpg" alt="Werkplaats TT Motorsports" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-[#E10600] px-6 py-4 hidden md:block">
              <p className="font-display uppercase font-800 text-3xl text-white leading-none">Alle merken</p>
              <p className="font-mono-tt text-xs text-white/80 mt-1">Onderhoud & herstelling</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section-pad bg-[#0D0D0F]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <h2 className="heading-xl text-4xl md:text-6xl text-white mb-14 text-center">Waar wij voor staan</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[#2A2B31] border border-[#2A2B31]">
            {values.map((v, i) => (
              <motion.div key={v.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="bg-[#0D0D0F] p-7" data-testid={`value-${i}`}>
                <v.icon size={28} className="text-[#E10600] mb-4" strokeWidth={1.5} />
                <h3 className="font-display uppercase font-700 text-lg text-white mb-2">{v.title}</h3>
                <p className="text-[#9A9CA3] text-sm leading-relaxed">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
